/**
 * 站内导航封装：真翻页前可先 release 活动弹窗（改状态 → 关 UI → 再改 pathname），
 * 并默认合并落地染色五参（与 useAppRouter / AppLink 一致）。
 * 同 path 仅 scrub query 时 scene 不变 → 不关弹窗，与防刷新清 URL 兼容。
 *
 * 后续开发：翻页优先用 useAppNavigate / useAppRouter / AppLink；漏网仍由 ActivitySceneSync 兜底。
 */

import { flushSync } from 'react-dom';

import { releaseCurrentPopup } from '@/activity-orchestration/lifecycle/release-current-popup';
import { resolveActiveScene } from '@/activity-orchestration/scene';
import { useActivityQueueStore } from '@/store/activity-queue';
import { withDyeQuery } from '@/utils/router/with-dye-query';

/** 透传给 next/navigation router.push / replace 的滚动选项 */
export interface IAppNavigateScrollOptions {
    scroll?: boolean;
}

/** 最小 Router 表面，避免依赖 next/dist 内部路径 */
export interface IAppRouterLike {
    push: (href: string, options?: IAppNavigateScrollOptions) => void;
    replace: (href: string, options?: IAppNavigateScrollOptions) => void;
    back?: () => void;
}

export interface IAppNavigateOptions {
    /** push（默认）或 replace */
    type?: 'push' | 'replace';
    /**
     * 跳过活动弹窗释放（仅 scrub URL、强制不关窗等）。
     * 默认同 path→同 scene 本就不会 release；此开关用于显式关闭释放逻辑。
     */
    skipActivityRelease?: boolean;
    /**
     * 是否把当前 URL 落地相关五参合并进目标 href，默认 true。
     * 仅在明确需要清参 / 不透传时传 false（如 AppLink 已合并过）。
     */
    preserveDye?: boolean;
    /** 透传给 next/navigation router.push/replace */
    scroll?: boolean;
}

/**
 * 从 href 取出 pathname（去掉 query / hash）。
 * @param href 站内路径或绝对 URL
 */
export function getPathnameFromHref(href: string): string {
    if (!href) {
        return '/';
    }
    try {
        if (href.startsWith('http://') || href.startsWith('https://')) {
            return new URL(href).pathname || '/';
        }
    } catch {
        // 非法 URL 时按相对路径解析
    }
    const withoutHash = href.split('#')[0] ?? href;
    const path = withoutHash.split('?')[0] ?? withoutHash;
    return path || '/';
}

/**
 * 是否应在跳转前释放当前活动弹窗。
 * 仅当存在 current 且目标 scene 与当前 activeScene 不同时返回 true。
 * @param href 目标地址
 */
export function shouldReleasePopupBeforeNavigate(href: string): boolean {
    const { current, activeScene } = useActivityQueueStore.getState();
    if (!current) {
        return false;
    }
    const nextScene = resolveActiveScene(getPathnameFromHref(href));
    return nextScene !== activeScene;
}

/**
 * 同步：改队列状态 → flush 卸掉弹窗 UI。
 */
function releasePopupUiSync(): void {
    const { current } = useActivityQueueStore.getState();
    if (!current) {
        return;
    }
    // flushSync：保证 Host 在本帧内根据 current=null 卸掉 Modal，再执行 push/replace
    flushSync(() => {
        releaseCurrentPopup('route_change', current.id);
    });
}

/**
 * 若需要则先 release 活动弹窗并卸 UI，再执行导航（默认合并染色五参）。
 * @param router Next App Router 实例（或兼容 push/replace 的对象）
 * @param href 目标地址
 * @param options 导航选项
 */
export function appNavigate(router: IAppRouterLike, href: string, options?: IAppNavigateOptions): void {
    const type = options?.type ?? 'push';
    const skip = options?.skipActivityRelease === true;
    const preserveDye = options?.preserveDye !== false;
    const finalHref = preserveDye ? withDyeQuery(href) : href;
    const willRelease = !skip && shouldReleasePopupBeforeNavigate(finalHref);
    const scrollOptions: IAppNavigateScrollOptions | undefined =
        options?.scroll === undefined ? undefined : { scroll: options.scroll };

    if (willRelease) {
        releasePopupUiSync();
    }

    if (type === 'replace') {
        router.replace(finalHref, scrollOptions);
        return;
    }
    router.push(finalHref, scrollOptions);
}

/**
 * 浏览器后退：有 current 时先 release 并卸 UI，再 back。
 * 无法预知下一 scene，保守在有弹窗时一律先关（漏关由 SceneSync 兜底）。
 * @param router 需带 back 的 Router
 */
export function appNavigateBack(router: IAppRouterLike): void {
    const { current } = useActivityQueueStore.getState();
    if (current) {
        releasePopupUiSync();
    }
    if (typeof router.back === 'function') {
        router.back();
    }
}
